import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SearchBar from "./SearchBar";
import { toast } from "react-toastify";
import vppCatalogService from "../services/vppCatalogService";
import AppIcon from "./AppIcon";
import { APP_BLACKLIST_GRACE_DAYS, appStatusClass, appStatusLabel } from "../utils/appBlacklist";

function formatBytes(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "—";
  if (n < 1024) return `${Math.round(n)} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export default function ApplicationsTable({ Applications, UDID, Administrator, onDataChanged }) {
  const [query, setQuery] = useState("");
  const [managed, setManaged] = useState([]);
  const [loadingManaged, setLoadingManaged] = useState(Boolean(UDID));
  const [busyKey, setBusyKey] = useState("");

  const loadManaged = useCallback(async () => {
    if (!UDID) return;
    setLoadingManaged(true);
    try {
      const data = await vppCatalogService.getDeviceApps(UDID);
      setManaged(data.apps || []);
    } catch {
      setManaged([]);
    } finally {
      setLoadingManaged(false);
    }
  }, [UDID]);

  useEffect(() => {
    loadManaged();
  }, [loadManaged, Applications]);

  const rows = useMemo(() => {
    if (managed.length > 0 || !loadingManaged) return managed;
    return (Applications || []).map((app) => ({
      adamId: null,
      bundleId: app.Identifier,
      name: app.Name,
      version: app.Version,
      artworkUrl: null,
      installed: app.Installing !== true,
      installing: app.Installing === true,
      occupiedBytes: (Number(app.BundleSize) || 0) + (Number(app.DynamicSize) || 0) || null,
      assigned: false,
      disabled: false,
      blacklisted: false,
      groups: [],
      config: null,
      configSource: "none",
      availableConfigs: [],
    }));
  }, [managed, Applications, loadingManaged]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((app) =>
      [app.name, app.bundleId, app.adamId, app.config?.name, ...(app.groups || [])]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [rows, query]);

  const run = async (key, fn, success) => {
    setBusyKey(key);
    try {
      await fn();
      toast.success(success);
      await loadManaged();
      if (onDataChanged) onDataChanged();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Aktion fehlgeschlagen.");
    } finally {
      setBusyKey("");
    }
  };

  const handleConfigChange = (app, value) => {
    if (!app.adamId) return;
    if (value === "__group__") {
      run(`cfg-${app.adamId}`, () => vppCatalogService.setDeviceAppConfig(UDID, app.adamId, {
        configOverridden: false,
        bundleId: app.bundleId,
      }), "Gruppen-Config wiederhergestellt.");
      return;
    }
    run(`cfg-${app.adamId}`, () => vppCatalogService.setDeviceAppConfig(UDID, app.adamId, {
      managedAppConfig: value || null,
      configOverridden: true,
      bundleId: app.bundleId,
    }), "Geräte-Config gespeichert.");
  };

  const blacklistConfirm = (app) => {
    const grace = APP_BLACKLIST_GRACE_DAYS;
    const extra = app.assigned
      ? " Bei Gruppenzuweisung wird die App für dieses Gerät während der Karenz nicht neu installiert."
      : "";
    return window.confirm(
      `App auf die Blacklist setzen? Sie bleibt ${grace} Tage auf dem Gerät und wird danach automatisch deinstalliert.${extra}`
    );
  };

  return (
    <div>
      {Administrator ? (
        <p className="small text-muted mb-2">
          Entfernen setzt Apps zunächst auf die Blacklist ({APP_BLACKLIST_GRACE_DAYS} Tage Karenz).
          Währenddessen bleiben sie installiert und können reaktiviert oder sofort deinstalliert werden.
        </p>
      ) : null}
      <table className="table tableList">
        <thead>
          <tr>
            <th>
              <SearchBar searchHandler={setQuery} value={query} placeholder="App suchen" />
            </th>
            <th>Identifier</th>
            <th>Version</th>
            <th>Speicher</th>
            <th>Status</th>
            <th>Managed App Config</th>
            {Administrator && <th></th>}
          </tr>
        </thead>
        <tbody>
          {loadingManaged && visible.length === 0 ? (
            <tr>
              <td colSpan={Administrator ? 7 : 6} className="text-muted">
                Lade zugewiesene Apps…
              </td>
            </tr>
          ) : null}
          {!loadingManaged && visible.length === 0 ? (
            <tr>
              <td colSpan={Administrator ? 7 : 6} className="text-muted">
                Keine zugewiesenen oder inventarisierten Apps.
              </td>
            </tr>
          ) : null}
          {visible.map((app, index) => {
            const key = app.adamId || app.bundleId || String(index);
            const configValue = app.configSource === "device"
              ? (app.config?.id || "")
              : "__group__";
            return (
              <tr key={key}>
                <td>
                  <div className="d-flex align-items-center gap-2">
                    <AppIcon src={app.artworkUrl} name={app.name} size={36} />
                    <div>
                      <div>
                        {app.adamId ? (
                          <Link
                            to={`/applications/${app.adamId}`}
                            className="text-decoration-none"
                          >
                            {app.name || app.bundleId || app.adamId}
                          </Link>
                        ) : (
                          app.name || app.bundleId || "—"
                        )}
                        {app.isB2BCustomApp ? (
                          <span className="badge text-bg-info ms-2">Custom B2B</span>
                        ) : null}
                      </div>
                      {app.groups?.length > 0 && (
                        <div className="small text-muted">Gruppe: {app.groups.join(", ")}</div>
                      )}
                    </div>
                  </div>
                </td>
                <td><code>{app.bundleId || "—"}</code></td>
                <td>{app.version || "—"}</td>
                <td>{formatBytes(app.occupiedBytes)}</td>
                <td>
                  <span className={`badge ${appStatusClass(app)}`}>{appStatusLabel(app)}</span>
                </td>
                <td>
                  {app.adamId && Administrator ? (
                    <select
                      className="form-select form-select-sm"
                      value={configValue}
                      disabled={busyKey === `cfg-${app.adamId}` || app.blacklisted}
                      onChange={(e) => handleConfigChange(app, e.target.value)}
                    >
                      <option value="__group__">
                        Gruppen-Vorgabe{app.configSource !== "device" && app.config?.name ? ` (${app.config.name})` : ""}
                      </option>
                      <option value="">Keine Config (Gerät)</option>
                      {(app.availableConfigs || []).map((cfg) => (
                        <option key={cfg.id} value={cfg.id}>{cfg.name} (Gerät)</option>
                      ))}
                    </select>
                  ) : (
                    <span className="small">
                      {app.config?.name
                        ? `${app.config.name}${app.configSource === "device" ? " · Gerät" : " · Gruppe"}`
                        : "—"}
                    </span>
                  )}
                </td>
                {Administrator && (
                  <td className="text-nowrap">
                    {app.adamId ? (
                      <div className="d-flex flex-wrap gap-1">
                        {app.blacklisted ? (
                          <>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-success"
                              disabled={Boolean(busyKey)}
                              onClick={() => run(
                                `act-${app.adamId}`,
                                () => vppCatalogService.activateDeviceApp(UDID, app.adamId),
                                "App wieder aktiviert."
                              )}
                            >
                              Aktivieren
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              disabled={Boolean(busyKey)}
                              onClick={() => {
                                if (!window.confirm("App sofort deinstallieren (ohne Karenzzeit)?")) return;
                                run(
                                  `force-${app.adamId}`,
                                  () => vppCatalogService.removeDeviceApp(UDID, app.adamId, {
                                    force: true,
                                    disable: app.disabled,
                                    bundleId: app.bundleId,
                                  }),
                                  "Sofortige Deinstallation angestoßen."
                                );
                              }}
                            >
                              Sofort deinstallieren
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              className={`btn btn-sm ${
                                app.assigned && !app.installed && !app.installing
                                  ? "btn-primary"
                                  : "btn-outline-primary"
                              }`}
                              disabled={Boolean(busyKey)}
                              title={
                                app.assigned && !app.installed && !app.installing
                                  ? "Zugewiesen, aber nicht auf dem Gerät — Installation erneut anstoßen"
                                  : "Installation erneut anstoßen (auch bei hängender Installation)"
                              }
                              onClick={() => run(
                                `re-${app.adamId}`,
                                () => vppCatalogService.reinstallDeviceApp(UDID, app.adamId),
                                "Erneute Installation angestoßen."
                              )}
                            >
                              Erneut installieren
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-warning"
                              disabled={Boolean(busyKey)}
                              onClick={() => {
                                if (!blacklistConfirm(app)) return;
                                run(
                                  `bl-${app.adamId}`,
                                  () => vppCatalogService.removeDeviceApp(UDID, app.adamId, {
                                    bundleId: app.bundleId,
                                  }),
                                  `App auf Blacklist gesetzt (${APP_BLACKLIST_GRACE_DAYS} Tage Karenz).`
                                );
                              }}
                            >
                              Blacklist
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              disabled={Boolean(busyKey)}
                              onClick={() => {
                                if (!window.confirm(
                                  `App auf Blacklist setzen und danach keine automatische Installation mehr? Nach ${APP_BLACKLIST_GRACE_DAYS} Tagen wird sie deinstalliert.`
                                )) return;
                                run(
                                  `dis-${app.adamId}`,
                                  () => vppCatalogService.removeDeviceApp(UDID, app.adamId, {
                                    disable: true,
                                    bundleId: app.bundleId,
                                  }),
                                  "Blacklist mit Installations-Sperre gesetzt."
                                );
                              }}
                            >
                              Blacklist + Sperre
                            </button>
                          </>
                        )}
                      </div>
                    ) : null}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
